import React from 'react';
import './PrintableFinancialReport.css'; // Assuming a similar CSS file structure
import { format } from 'date-fns';

// Interfaces based on data structure from report.controller.js getFinancialReport
interface ReportSummary {
  totalRevenue: number;
  totalPayments: number;
  totalExpenses: number;
  profit: number;
  accountsReceivable: number;
  profitMargin: number;
  pendingOrders: number;
}

interface RevenueByClient {
  clientId: number;
  totalAmount: number;
  'Client.name': string; // Adjust based on actual raw query result key
}

interface ExpensesByCategory {
  category: string;
  totalAmount: number;
}

interface UnpaidOrder {
    id: number;
    orderNumber: string;
    clientId: number;
    finalAmount: number;
    paidAmount: number;
    createdAt: string;
    Client: {
        name: string;
        director?: string | null;
        phone?: string | null;
    };
}

interface FinancialReportData {
  summary: ReportSummary;
  revenueByClient: RevenueByClient[];
  expensesByCategory: ExpensesByCategory[];
  unpaidOrders: UnpaidOrder[];
  startDate: string; // Need to pass these from the print page component
  endDate: string;   // Need to pass these from the print page component
}

interface PrintableFinancialReportProps {
  reportData: FinancialReportData;
}

// Helper to format dates
const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'dd.MM.yyyy');
  } catch (e) {
    return 'Invalid Date';
  }
};

// Helper to format currency
const formatCurrency = (value: number | null | undefined): string => {
  if (value === undefined || value === null) return '0 ₸';
  return new Intl.NumberFormat('ru-KZ', {
    style: 'currency',
    currency: 'KZT',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const PrintableFinancialReport: React.FC<PrintableFinancialReportProps> = ({ reportData }) => {
  if (!reportData) {
    return <div className="printable-container">Ошибка: Данные для финансового отчета не найдены.</div>;
  }

  const {
    summary,
    revenueByClient,
    expensesByCategory,
    unpaidOrders,
    startDate,
    endDate,
  } = reportData;

  return (
    <div className="printable-container financial-report-container">
      <header className="printable-header">
        <h1>Финансовый отчет</h1>
        <p>Период: {formatDate(startDate)} - {formatDate(endDate)}</p>
      </header>

      <section className="summary-section">
        <h2>Сводка</h2>
        <div className="summary-grid">
          <div><strong>Доход (по заказам):</strong> {formatCurrency(summary.totalRevenue)}</div>
          <div><strong>Платежи получено:</strong> {formatCurrency(summary.totalPayments)}</div>
          <div><strong>Расходы:</strong> {formatCurrency(summary.totalExpenses)}</div>
          <div><strong>Прибыль:</strong> {formatCurrency(summary.profit)}</div>
          <div><strong>Рентабельность:</strong> {summary.profitMargin.toFixed(1)}%</div>
          <div><strong>Дебиторская задолженность:</strong> {formatCurrency(summary.accountsReceivable)}</div>
          <div><strong>Ожидающие заказы (шт):</strong> {summary.pendingOrders}</div>
        </div>
      </section>

      {revenueByClient && revenueByClient.length > 0 && (
        <section className="details-table">
          <h2>Доход по клиентам</h2>
          <table>
            <thead>
              <tr>
                <th>Клиент</th>
                <th>Сумма дохода</th>
              </tr>
            </thead>
            <tbody>
              {revenueByClient.map((clientData, index) => (
                <tr key={index}>
                  <td>{clientData['Client.name'] || `ID: ${clientData.clientId}`}</td>
                  <td>{formatCurrency(clientData.totalAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {expensesByCategory && expensesByCategory.length > 0 && (
        <section className="details-table">
          <h2>Расходы по категориям</h2>
          <table>
            <thead>
              <tr>
                <th>Категория</th>
                <th>Сумма расходов</th>
              </tr>
            </thead>
            <tbody>
              {expensesByCategory.map((expenseData, index) => (
                <tr key={index}>
                  <td>{expenseData.category}</td>
                  <td>{formatCurrency(expenseData.totalAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

       {unpaidOrders && unpaidOrders.length > 0 && (
        <section className="details-table">
          <h2>Дебиторская задолженность (Неоплаченные заказы)</h2>
          <table>
            <thead>
              <tr>
                <th>Заказ №</th>
                <th>Клиент</th>
                <th>Дата заказа</th>
                <th>Сумма заказа</th>
                <th>Оплачено</th>
                <th>Остаток</th>
              </tr>
            </thead>
            <tbody>
              {unpaidOrders.map((order) => (
                <tr key={order.id}>
                  <td>{order.orderNumber}</td>
                  <td>{order.Client?.name || `ID: ${order.clientId}`}</td>
                  <td>{formatDate(order.createdAt)}</td>
                  <td>{formatCurrency(order.finalAmount)}</td>
                  <td>{formatCurrency(order.paidAmount)}</td>
                  <td>{formatCurrency(order.finalAmount - order.paidAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <footer className="printable-footer">
        Отчет сформирован: {formatDate(new Date())}
      </footer>
    </div>
  );
};

export default PrintableFinancialReport;
